import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { getPlatformSettings, updatePlatformSettings } from '@/services'
import { AppButton, Skeleton, Tabs } from '@/components/platform/primitives'
import { Icon } from '@/components/platform/Icon'

export function SettingsPage() {
  const qc = useQueryClient()
  const [tab, setTab] = useState('maps')
  const { data: settings, isLoading } = useQuery({ queryKey: ['platform-settings'], queryFn: getPlatformSettings })

  const saveMutation = useMutation({
    mutationFn: (body: Parameters<typeof updatePlatformSettings>[0]) => updatePlatformSettings(body),
    onSuccess: () => { toast.success('Configuración guardada'); qc.invalidateQueries({ queryKey: ['platform-settings'] }) },
    onError: (e: any) => toast.error(e?.message || 'No se pudo guardar'),
  })

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="eyebrow">Plataforma</div>
          <h1>Settings globales</h1>
          <div className="subtitle">Integraciones que comparten todos los tenants. Las API keys nunca se devuelven en plano.</div>
        </div>
      </div>

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'maps', label: 'Google Maps', icon: 'globe' },
          { id: 'gemini', label: 'Gemini AI', icon: 'cpu' },
          { id: 'meta', label: 'Meta Ads', icon: 'shoppingBag' },
        ]}
      />

      {tab === 'maps' ? (
        isLoading ? <SettingsCardSkeleton title="Google Maps API" /> : <KeyCard
          title="Google Maps API"
          desc="Autocomplete de direcciones, geocoding y mapas en el admin de los tenants."
          configured={!!settings?.google_maps_api_key_configured}
          onSave={(key) => saveMutation.mutate({ google_maps_api_key: key })}
          saving={saveMutation.isPending}
        />
      ) : null}

      {tab === 'gemini' ? (
        isLoading ? <SettingsCardSkeleton title="Gemini AI" /> : <KeyCard
          title="Gemini AI"
          desc="Generación de descripciones, sugerencias de precios y análisis de stock."
          configured={!!settings?.gemini_api_key_configured}
          onSave={(key) => saveMutation.mutate({ gemini_api_key: key })}
          saving={saveMutation.isPending}
        />
      ) : null}

      {tab === 'meta' ? (
        isLoading ? <SettingsCardSkeleton title="Meta Pixel + Conversions API" /> : <div className="card">
          <div className="card-header">
            <h3>Meta Pixel + Conversions API</h3>
            <label className="toggle">
              <input type="checkbox" checked={!!settings?.meta_ads_enabled} onChange={(e) => saveMutation.mutate({ meta_ads_enabled: e.target.checked })} />
              {settings?.meta_ads_enabled ? 'Activado' : 'Desactivado'}
            </label>
          </div>
          <div className="card-body">
            <p className="muted-2" style={{ fontSize: 13.5, marginTop: 0, marginBottom: 18 }}>Tracking del funnel de signup y conversiones en bisne.app. El Pixel ID es público; el CAPI token queda solo en server.</p>
            <form onSubmit={(e) => {
              e.preventDefault()
              const fd = new FormData(e.currentTarget)
              saveMutation.mutate({
                meta_pixel_id: String(fd.get('meta_pixel_id') || '') || null,
                meta_test_event_code: String(fd.get('meta_test_event_code') || '') || null,
                meta_capi_token: String(fd.get('meta_capi_token') || '') || undefined,
              })
            }} className="col gap-4">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="form-row"><label htmlFor="meta-pixel">Pixel ID (público)</label><input id="meta-pixel" className="input" name="meta_pixel_id" defaultValue={settings?.meta_pixel_id || ''} /></div>
                <div className="form-row"><label htmlFor="meta-test">Test event code</label><input id="meta-test" className="input" name="meta_test_event_code" defaultValue={settings?.meta_test_event_code || ''} placeholder="TEST12345 (opcional)" /></div>
              </div>
              <div className="form-row">
                <label htmlFor="meta-capi">CAPI token {settings?.meta_capi_token_configured ? '(configurado — dejá vacío para no cambiarlo)' : ''}</label>
                <input id="meta-capi" className="input" name="meta_capi_token" type="password" autoComplete="off" placeholder={settings?.meta_capi_token_configured ? '•••••••• configurado' : 'pegá el token…'} />
              </div>
              <AppButton type="submit" variant="primary" size="sm" icon="check" disabled={saveMutation.isPending}>Guardar Meta</AppButton>
            </form>
          </div>
        </div>
      ) : null}
    </>
  )
}

function SettingsCardSkeleton({ title }: { title: string }) {
  return (
    <div className="card">
      <div className="card-header"><h3>{title}</h3></div>
      <div className="card-body">
        <Skeleton width="78%" height={16} style={{ marginBottom: 18 }} />
        <Skeleton width="100%" height={42} style={{ marginBottom: 12 }} />
        <Skeleton width="46%" height={12} />
      </div>
    </div>
  )
}

function KeyCard({ title, desc, configured, onSave, saving }: { title: string; desc: string; configured: boolean; onSave: (key: string) => void; saving: boolean }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')
  return (
    <div className="card">
      <div className="card-header"><h3>{title}</h3></div>
      <div className="card-body">
        <p className="muted-2" style={{ fontSize: 13.5, marginTop: 0, marginBottom: 18 }}>{desc}</p>
        <div className="form-row">
          <label htmlFor="keycard-key">API key</label>
          {configured && !editing ? (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 12px', background: 'var(--success-bg)', borderRadius: 8, border: '1px solid var(--success-border)', color: 'var(--success-fg)' }}>
              <Icon name="check" size={14} />
              <span style={{ flex: 1, fontWeight: 600 }}>Configurada — la key está cifrada en la DB</span>
              <AppButton variant="secondary" size="sm" onClick={() => setEditing(true)}>Cambiar</AppButton>
            </div>
          ) : (
            <div className="row gap-2">
              <input id="keycard-key" className="input" type="password" autoComplete="off" value={value} onChange={(e) => setValue(e.target.value)} placeholder="pegá la key acá…" />
              <AppButton variant="primary" size="sm" icon="check" disabled={saving || !value.trim()} onClick={() => { onSave(value.trim()); setValue(''); setEditing(false) }}>Guardar</AppButton>
            </div>
          )}
        </div>
        <div className="hint">Por seguridad, una vez guardada no la mostramos de vuelta. Solo podés reemplazarla.</div>
      </div>
    </div>
  )
}
